import { useCallback, useEffect, useMemo, useState } from "react";
import { useArbitrumLatest } from "../../hooks/useArbitrumLatest";
import { useArbitrumManualHistory } from "../../hooks/useArbitrumManualHistory";
import { useSettings } from "../../contexts/SettingsContext";
import { FadingRuler } from "../shared/FadingRuler";
import { DotMatrixLoader } from "../icon-bank/DotMatrixLoader";
import { VerdictCard } from "./VerdictCard";
import { SeatCard, EmptySeat } from "./ChamberSeats";
import { ArbitrumChamberHeader } from "./ArbitrumChamberHeader";
import { ArbitrumConfidencePair } from "./ArbitrumConfidencePair";
import { ArbitrumPresetPanel } from "./ArbitrumPresetPanel";
import { ArbitrumExpandPanel } from "./ArbitrumExpandPanel";
import { ArbitrumSettingsPanel } from "./ArbitrumSettingsPanel";
import { ChamberAgentSummaryPopup } from "./ChamberAgentSummaryPopup";
import { useArbitrumPanelState } from "./useArbitrumPanelState";
import { useStaggeredReveal } from "./useStaggeredReveal";
import type { ArbitrumSeat, ArbitrumVerdict } from "./types";

interface ArbitrumChamberProps {
  /** Unused here (kept for API parity with the retired AgentDeskDebatePanel). */
  simulationId?: string | null;
  /** Fires when a verdict with phase === "complete" first appears. */
  onSynthesisComplete?: () => void;
}

const DEFAULT_ROLES: ReadonlyArray<ArbitrumSeat["role"]> = [
  "Lead",
  "Forecaster",
  "Future PM",
  "Quant",
  "Skeptic",
];

const AGENT_ROW_ROLES: ReadonlyArray<ArbitrumSeat["role"]> = [
  "Forecaster",
  "Future PM",
  "Quant",
  "Skeptic",
];

const EMPTY_COPY =
  "No fresh read — chamber convenes at 17:00 ET or on IV ≥ 8.5.";

export function ArbitrumChamber(props: ArbitrumChamberProps) {
  const { onSynthesisComplete } = props;
  const { selectedInstrument, setSelectedInstrument } = useSettings();
  const { verdict, isLoading, error, refresh } =
    useArbitrumLatest(selectedInstrument);
  const manualHistory = useArbitrumManualHistory();
  const refreshManualHistory = manualHistory.refresh;
  const panels = useArbitrumPanelState();
  const {
    settingsOpen,
    presetsOpen,
    expandOpen,
    selectedPresetIds,
    setSettingsOpen,
    closePresets,
    closeExpand,
    openExpand: openExpandPanel,
    openPresets: openPresetsPanel,
    toggleSettings,
  } = panels;
  const [openSummaryRoles, setOpenSummaryRoles] = useState<
    ArbitrumSeat["role"][]
  >([]);

  const seats = useMemo<ArbitrumSeat[]>(() => {
    const supplied = verdict?.seats ?? [];
    if (supplied.length >= 5) return supplied.slice(0, 5);
    const bySlot = new Map(supplied.map((s) => [s.role, s] as const));
    return DEFAULT_ROLES.map(
      (role): ArbitrumSeat =>
        bySlot.get(role) ?? {
          role,
          model: "—",
          probability: 0,
          confidence: 0,
          rationale: "",
        },
    );
  }, [verdict]);

  const caoSeat = useMemo(
    () => seats.find((seat) => seat.role === "Lead") ?? null,
    [seats],
  );

  const agentRowSeats = useMemo<ArbitrumSeat[]>(() => {
    const byRole = new Map(seats.map((seat) => [seat.role, seat] as const));
    return AGENT_ROW_ROLES.map(
      (role): ArbitrumSeat =>
        byRole.get(role) ?? {
          role,
          model: "—",
          probability: 0,
          confidence: 0,
          rationale: "",
        },
    );
  }, [seats]);

  const revealed = useStaggeredReveal(agentRowSeats.length);

  const openSummaries = useMemo(
    () =>
      openSummaryRoles
        .map((role) => agentRowSeats.find((seat) => seat.role === role))
        .filter((seat): seat is ArbitrumSeat => Boolean(seat?.rationale)),
    [openSummaryRoles, agentRowSeats],
  );

  const openAgentSummary = useCallback((role: ArbitrumSeat["role"]) => {
    setOpenSummaryRoles((prev) => {
      const next = [...prev.filter((item) => item !== role), role];
      return next.slice(-2);
    });
  }, []);

  const closeAgentSummary = useCallback((role: ArbitrumSeat["role"]) => {
    setOpenSummaryRoles((prev) => prev.filter((item) => item !== role));
  }, []);

  const openExpand = useCallback(() => {
    setOpenSummaryRoles([]);
    openExpandPanel();
  }, [openExpandPanel]);

  const openPresets = useCallback(() => {
    setOpenSummaryRoles([]);
    openPresetsPanel();
  }, [openPresetsPanel]);

  const roundsTotal = verdict?.rounds_total ?? 3;
  const roundsComplete =
    verdict?.rounds_complete ?? (verdict ? roundsTotal : 0);
  const roundsValue = roundsTotal > 0 ? roundsComplete / roundsTotal : 0;
  const phase = verdict?.phase ?? (verdict ? "complete" : "convening");
  const [phaseNotice, setPhaseNotice] = useState<string | null>(null);

  useEffect(() => {
    setPhaseNotice(phase);
    const id = window.setTimeout(() => setPhaseNotice(null), 2600);
    return () => window.clearTimeout(id);
  }, [phase]);

  // Fire onSynthesisComplete once per verdict id when phase === "complete".
  const [firedFor, setFiredFor] = useState<string | null>(null);
  useEffect(() => {
    if (!verdict || phase !== "complete") return;
    if (firedFor === verdict.id) return;
    setFiredFor(verdict.id);
    onSynthesisComplete?.();
  }, [verdict, phase, firedFor, onSynthesisComplete]);

  useEffect(() => {
    setOpenSummaryRoles([]);
    void refreshManualHistory();
  }, [verdict?.id, refreshManualHistory]);

  const hasVerdict = Boolean(verdict);
  const hasRealSeats = (verdict?.seats?.length ?? 0) > 0;
  return (
    <div className="relative flex flex-col min-h-0 min-w-0 gap-2.5">
      <div
        className={`flex min-h-0 min-w-0 flex-col gap-2.5 transition-opacity duration-500 ${
          presetsOpen ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <ArbitrumChamberHeader
          selectedInstrument={selectedInstrument}
          phaseNotice={phaseNotice}
          manualHistory={manualHistory}
          onInstrumentChange={setSelectedInstrument}
          onOpenExpand={openExpand}
          onOpenPresets={openPresets}
          onToggleSettings={toggleSettings}
        />

        <div className="grid grid-cols-1 gap-0 md:grid-cols-4">
          {agentRowSeats.map((seat, i) => (
            <div
              key={`${verdict?.id ?? "empty"}-${seat.role}-${i}`}
              className="min-w-0"
            >
              {i > 0 && <FadingRuler className="my-1 md:hidden" />}
              {hasRealSeats ? (
                <SeatCard
                  seat={seat}
                  index={i}
                  visible={revealed[i] ?? false}
                  isSummaryOpen={openSummaryRoles.includes(seat.role)}
                  onOpenSummary={() => openAgentSummary(seat.role)}
                />
              ) : (
                <EmptySeat
                  role={seat.role}
                  index={i}
                  visible={revealed[i] ?? false}
                />
              )}
            </div>
          ))}
        </div>

        {hasVerdict && (
          <ArbitrumConfidencePair
            caoConfidence={caoSeat?.confidence ?? 0}
            chamberConfidence={verdict?.confidence ?? 0}
          />
        )}

        {hasVerdict && (
          <VerdictCard verdict={verdict as ArbitrumVerdict} compact embedded />
        )}

        {!hasVerdict && (
          <div className="bg-transparent p-3 text-xs text-[var(--fintheon-text)]/55">
            <FadingRuler className="mb-3" />
            {isLoading ? (
              <DotMatrixLoader
                variant="pyramid"
                size={24}
                label="Loading chamber read"
              />
            ) : error ? (
              <div className="flex flex-col gap-1.5">
                <span>Chamber unreachable ({error})</span>
                <button
                  onClick={() => void refresh()}
                  disabled={isLoading}
                  className="self-start border border-[var(--fintheon-accent)]/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/10 disabled:opacity-40"
                >
                  Retry
                </button>
              </div>
            ) : (
              EMPTY_COPY
            )}
          </div>
        )}
      </div>

      {settingsOpen && (
        <ArbitrumSettingsPanel onClose={() => setSettingsOpen(false)} />
      )}

      {presetsOpen && (
        <ArbitrumPresetPanel onClose={closePresets} onSaved={closePresets} />
      )}

      {expandOpen && (
        <ArbitrumExpandPanel
          presetIds={selectedPresetIds}
          selectedInstrument={selectedInstrument}
          onClose={closeExpand}
        />
      )}

      {!presetsOpen &&
        !expandOpen &&
        openSummaries.map((seat, index) => (
          <ChamberAgentSummaryPopup
            key={seat.role}
            seat={seat}
            index={index}
            zIndex={70 + index}
            onActivate={() => openAgentSummary(seat.role)}
            onClose={() => closeAgentSummary(seat.role)}
          />
        ))}
    </div>
  );
}

export default ArbitrumChamber;
